import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import {
  listNotesApi,
  updateNoteApi,
  markViewedApi,
  deleteNoteApi
} from '../../utils/notesData';
import { useDispatch } from 'react-redux';
import { setErrorAlert, setSuccessAlert } from '../../store/alert/alertSlice';
import { canDeleteContent } from '../../utils/permissions';

const NotesApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadNotes();
  }, [activeTab, selectedCategory, searchQuery]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const fetched = await listNotesApi({
        q: searchQuery || undefined,
        category: activeTab === 'categories' ? (selectedCategory || undefined) : undefined,
        bookmarked: activeTab === 'bookmarked' ? true : undefined,
      });
      setNotes(fetched);
    } catch (e) {
      console.error(e);
      dispatch(setErrorAlert('Failed to load notes'));
    } finally {
      setLoading(false);
    }
  };

  const categoriesWithCounts = useMemo(() => {
    const counts = {};
    notes.forEach(n => (n.categories || []).forEach(c => { counts[c] = (counts[c] || 0) + 1; }));
    return counts;
  }, [notes]);

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'bookmarked', label: 'Bookmarked' },
    { id: 'recent', label: 'Recently Viewed' },
    { id: 'categories', label: 'Categories' }
  ];

  const filteredNotes = notes;

  const handleNoteClick = (id) => {
    try {
      markViewedApi(id);
      navigate(`/notes/${id}`);
    } catch (e) {
      console.error(e);
      dispatch(setErrorAlert('Failed to open note'));
    }
  };

  const handleBookmarkToggle = async (id, e) => {
    e.stopPropagation();
    try {
      const current = notes.find(n => n._id === id);
      const updated = await updateNoteApi(id, { bookmarked: !current?.bookmarked });
      setNotes(prev => prev.map(n => (n._id === updated._id ? updated : n)));
      dispatch(setSuccessAlert(updated.bookmarked ? 'Note bookmarked!' : 'Bookmark removed'));
    } catch (e2) {
      console.error(e2);
      dispatch(setErrorAlert('Failed to update bookmark'));
    }
  };

  const handleCreateNote = () => {
    // Navigate to the new note route without creating it yet
    navigate('/notes/new');
  };

  const handleItemSelect = (note) => {
    setSelectedItems(prev => {
      const isSelected = prev.some(item => item._id === note._id);
      if (isSelected) {
        return prev.filter(item => item._id !== note._id);
      } else {
        return [...prev, note];
      }
    });
  };

  const handleSelectAll = () => {
    const allSelected = filteredNotes.every(note => selectedItems.some(item => item._id === note._id));
    
    if (allSelected) {
      // Deselect all current items
      setSelectedItems(prev => prev.filter(item => !filteredNotes.some(note => note._id === item._id)));
    } else {
      // Select all current items
      const newSelections = filteredNotes.filter(note => !selectedItems.some(item => item._id === note._id));
      setSelectedItems(prev => [...prev, ...newSelections]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;

    setIsDeleting(true);
    let successCount = 0;
    let failCount = 0;

    for (const item of selectedItems) {
      try {
        await deleteNoteApi(item._id);
        successCount++;
      } catch (error) {
        console.error(`Failed to delete note ${item.title}:`, error);
        failCount++;
      }
    }

    if (successCount > 0) {
      dispatch(setSuccessAlert(`Successfully deleted ${successCount} note(s)${failCount > 0 ? `, ${failCount} failed` : ''}`));
      setSelectedItems([]);
      loadNotes(); // Reload data
    } else {
      dispatch(setErrorAlert('Failed to delete selected notes. Please try again.'));
    }

    setIsDeleting(false);
    setShowDeleteConfirm(false);
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <div className="w-full px-4">
              <div className="text-white text-[14px] font-normal mb-2 text-left">
                Loading notes...
              </div>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#5856D6]"></div>
              </div>
            </div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Notes</span>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowSearch(!showSearch)}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search Notes
              </button>
              
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={handleCreateNote}
              >
                New Note
              </span>
              
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate(-1)}
              >
                Go back
              </span>
            </div>
          </div>

          {showSearch && (
            <div className="w-full px-4 mb-4">
              <div className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-[#7C4DFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Search Notes</span>
                </div>
                
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search your notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full bg-black border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] placeholder-[rgba(255,255,255,0.5)] focus:outline-none focus:border-[#7C4DFF]"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'categories' && (
            <div className="w-full px-4 mb-4">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full bg-[rgba(255,255,255,0.1)] border border-[rgba(255,255,255,0.2)] rounded-lg px-3 py-2 text-white text-[12px] focus:outline-none focus:border-[#7C4DFF]"
              >
                <option value="">All Categories</option>
                {Object.entries(categoriesWithCounts).map(([name, count]) => (
                  <option key={name} value={name} className="bg-black">
                    {name} ({count})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="w-full mb-6">
            <div className="flex overflow-x-auto scrollbar-hide px-4">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#7C4DFF]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full px-4 flex-1 overflow-y-auto">
            {filteredNotes.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-normal mb-2">No notes found</h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  {searchQuery ? `No notes match "${searchQuery}".` : 'Create your first note to get started.'}
                </p>
                <button 
                  onClick={handleCreateNote}
                  className="bg-[#7C4DFF] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[rgba(124,77,255,0.9)] transition-colors"
                >
                  New Note
                </button>
              </div>
            ) : (
              <>
                {/* Select All & Delete Buttons */}
                {filteredNotes.length > 0 && canDeleteContent('notes') && (
                  <div className="flex items-center justify-end px-4 mb-4 gap-3">
                    <button
                      onClick={handleSelectAll}
                      className="text-[#7C4DFF] text-[10px] hover:text-[#8E5CFF] transition-colors"
                    >
                      {filteredNotes.every(note => selectedItems.some(item => item._id === note._id)) ? 'Deselect All' : 'Select All'}
                    </button>
                    {selectedItems.length > 0 && (
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="bg-[#FF2300] text-white text-[10px] px-3 py-1.5 rounded-lg hover:bg-[#FF2300]/90 transition-colors"
                      >
                        Delete ({selectedItems.length})
                      </button>
                    )}
                  </div>
                )}

                <div className="grid gap-4">
                  {filteredNotes.map((note) => {
                    const isSelected = selectedItems.some(item => item._id === note._id);
                    return (
                      <div
                        key={note._id}
                        className={`bg-[rgba(255,255,255,0.05)] border rounded-lg p-4 transition-all cursor-pointer ${
                          isSelected 
                            ? 'border-[#7C4DFF] bg-[rgba(124,77,255,0.1)]' 
                            : 'border-[rgba(255,255,255,0.1)] hover:border-[#7C4DFF] hover:bg-[rgba(124,77,255,0.05)]'
                        }`}
                        onClick={() => canDeleteContent('notes') ? handleItemSelect(note) : handleNoteClick(note._id)}
                        style={{ cursor: canDeleteContent('notes') ? 'pointer' : 'default' }}
                      >
                        <div className="flex items-start gap-4">
                          {/* Note Icon/Indicator */}
                          <div className="flex flex-col items-center min-w-[60px]">
                            <div className="w-12 h-12 bg-[rgba(124,77,255,0.2)] rounded-lg flex items-center justify-center">
                              <svg className="w-6 h-6 text-[#7C4DFF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          </div>
                          
                          {/* Note Info */}
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="text-white text-[12px] font-normal break-words">
                                {note.title || 'Untitled Note'}
                              </h3>
                              <div className="flex items-center gap-2">
                                {canDeleteContent('notes') && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleNoteClick(note._id);
                                    }}
                                    className="text-[#7C4DFF] text-[10px] px-2 py-1 hover:bg-[rgba(124,77,255,0.1)] rounded transition-colors"
                                  >
                                    Edit
                                  </button>
                                )}
                                <button
                                  onClick={(e) => handleBookmarkToggle(note._id, e)}
                                  className={`p-1 rounded transition-colors ${
                                    note.bookmarked
                                      ? 'text-yellow-400 hover:text-yellow-300'
                                      : 'text-[rgba(255,255,255,0.3)] hover:text-yellow-400'
                                  }`}
                                >
                                  <svg className="w-4 h-4" fill={note.bookmarked ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                                  </svg>
                                </button>
                              </div>
                            </div>

                            <p className="text-[rgba(255,255,255,0.6)] text-[11px] mb-2 leading-tight break-words">
                              {(note.content || '').replace(/\n/g, ' ').slice(0, 120)}{(note.content || '').length > 120 ? '…' : ''}
                            </p>

                            {/* Tags and metadata row */}
                            <div className="flex flex-wrap items-center gap-2">
                              {/* Date and view count */}
                              <span className="text-[10px] text-[rgba(255,255,255,0.4)]">
                                {new Date(note.updatedAt).toLocaleDateString()}
                              </span>
                              {note.viewCount > 0 && (
                                <span className="text-[10px] text-[rgba(255,255,255,0.4)]">
                                  {note.viewCount} views
                                </span>
                              )}
                              
                              {/* Category tags */}
                              {note.categories?.slice(0, 3).map((cat) => (
                                <span key={cat} className="text-[9px] px-2 py-1 rounded bg-[rgba(124,77,255,0.2)] text-[rgba(124,77,255,1)] border border-[rgba(124,77,255,0.3)]">
                                  {cat}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-full max-w-[350px] mx-4 transform overflow-hidden rounded-2xl bg-black border-2 border-[#FF2300] p-6 text-left align-middle shadow-xl transition-all">
            {/* Header */}
            <div className="flex items-center justify-between mb-6 relative">
              <h3 className="text-white text-[16px] font-normal text-left">Delete Selected Notes</h3>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <p className="text-white text-[14px] mb-6">
              Are you sure you want to delete {selectedItems.length} note{selectedItems.length > 1 ? 's' : ''}? This action cannot be undone.
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="flex-1 bg-[#FF2300] text-white text-[12px] font-normal py-3 px-4 rounded-lg hover:bg-[#FF2300]/90 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isDeleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Deleting...
                  </>
                ) : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 bg-transparent border-2 border-[#FF2300] text-white text-[12px] font-normal py-3 px-4 rounded-lg hover:bg-[#FF2300]/10 transition-colors focus:outline-none focus:ring-2 focus:ring-[#FF2300] focus:ring-opacity-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutHeader>
  );
};

export default NotesApp;


